import { Crown, Check, ArrowRight, Sparkles, Calendar, Shield, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface TrialInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const trialFeatures = [
  'All Pro features unlocked',
  '50 generations per month',
  'All 4 social platforms',
  '3 post variations per platform',
  '8 workspaces',
  'AI image generation',
  'Company Intelligence',
  'No credit card required',
];

export function TrialInfoModal({ isOpen, onClose }: TrialInfoModalProps) {
  const navigate = useNavigate();

  const handleSignUp = () => {
    onClose();
    navigate('/register');
  };

  const handleLogin = () => {
    onClose();
    navigate('/login');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center pb-1">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-2">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <DialogTitle className="text-xl font-bold">
            Start Your 15-Day Free Trial
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Trial highlight */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-3 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-3 mb-1">
              <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <span className="font-semibold text-amber-900 dark:text-amber-100">
                Full Pro Access
              </span>
            </div>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Experience all premium features free for 15 days. Create psychology-powered posts that actually convert.
            </p>
          </div>

          {/* Features list */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-2">
              WHAT'S INCLUDED
            </h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {trialFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="text-sm text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What happens after */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
            <div className="flex items-center gap-3 mb-1">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium text-foreground">After 15 days</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Choose a plan that fits your needs. Plans start at just $10/month. You'll be prompted to select a tier to continue using Zupa.
            </p>
          </div>

          {/* Security note */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              No credit card required to start. Cancel anytime during your trial with no charges.
            </span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col gap-2 pt-1">
          <Button
            onClick={handleSignUp}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            size="lg"
          >
            Create Free Account
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          {/* Already have account option */}
          <Button
            variant="outline"
            onClick={handleLogin}
            className="w-full"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Already have an account? Log in
          </Button>

          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full h-8 text-muted-foreground text-sm"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
